import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { validityPeriodStringToInt } from "@features/port-security/components/utils";
import { api } from "@services/api";
import type {
  BulkCompleteSecurityClearanceRequest,
  BulkSecurityClearanceIdsRequest,
  BulkSecurityClearanceIdsWithReasonRequest,
  BulkSecurityClearanceResponse,
  BulkUpdateSecurityClearanceTasksArgs,
  CompleteSecurityClearancePayload,
  SecurityClearanceApiResponse,
  SecurityClearanceDetails,
  SecurityClearanceListItem,
  SecurityClearanceRequestPayload,
  SuspendSecurityClearanceByIdArgs,
} from "@services/security-clearance/types";
import { mapSecurityClearances } from "@utils/mappers/securityClearanceMapper";

interface GetSecurityClearancesParams {
  service: string;
  page?: number;
  size?: number;
  filters?: Record<string, any>;
}

/** React Query root key for the security clearances grid. */
export const SECURITY_CLEARANCES_QUERY_ROOT = "securityClearances" as const;

export const getSecurityClearancesQueryKey = (
  page: number,
  pageSize: number,
  filters: FilterItem[],
) => [SECURITY_CLEARANCES_QUERY_ROOT, page, pageSize, filters] as const;

// Latest approved clearance by application date
export function pickLatestApprovedSecurityClearanceId(
  securityClearances: SecurityClearanceListItem[] | undefined,
): string | null {
  if (!securityClearances?.length) return null;
  const approved = securityClearances
    .filter((item) => String(item.status ?? "").toUpperCase() === "APPROVED")
    .sort(
      (a, b) =>
        new Date(String(b.dateSubmitted ?? "")).getTime() -
        new Date(String(a.dateSubmitted ?? "")).getTime(),
    );
  const id = approved[0]?.id;
  const trimmed = id != null ? String(id).trim() : "";
  return trimmed || null;
}

interface GetSecurityClearanceByIdParams {
  id: string;
  service: string;
}

interface ClaimSecurityClearanceParams {
  service: string;
  securityClearanceId: string;
}

interface CancelSecurityClearanceParams {
  service: string;
  securityClearanceId: string;
}

export interface GatepassSecurityClearanceCheckResponse {
  valid?: boolean;
  securityClearanceId?: string;
  fullNameEnglish?: string;
  status?: string;
  approvalDate?: string;
  validityPeriod?: number;
  passportNumber?: string;
  presentNationality?: string;
  passportExpiryDate?: string;
  emiratesIdNumber?: string;
  requestingSponsorEnglish?: string;
  requestingSponsorArabic?: string;
  visitorCompanyNameEnglish?: string;
  visitorCompanyNameArabic?: string;
  entryPurposeEnglish?: string;
  fott?: boolean;
  expiryDate?: string;
  vehicleDetails?: {
    type?: string;
    drivingLicenseNumber?: string;
    placeOfIssue?: string;
    licensedBy?: string;
    issueDate?: string;
    expiryDate?: string;
    plateCode?: string;
    plateNo?: string;
  };
  mobileNumber?: string;
  visaType?: string;

  // Legacy fields kept optional for compatibility during BE transition
  expirationDate?: string;
  fullName?: string;
  nationality?: string;
  emiratesId?: string;
  phoneNumber?: string;
  email?: string;
  serialNo?: string;
  serialNumber?: string;
  id?: string;
}

export const getSecurityClearances = async ({
  service,
  page = 0,
  size = 4,
  filters = {},
}: GetSecurityClearancesParams): Promise<{
  securityClearances: SecurityClearanceListItem[];
  totalRecordCount: number;
}> => {
  const data: SecurityClearanceApiResponse = await api.post(
    service,
    `security-clearance-requests?pageSize=${size}&pageNumber=${page}`,
    {
      ...filters,
      ...(Array.isArray(filters.validityPeriods) &&
        filters.validityPeriods.length > 0 && {
          validityPeriods: filters.validityPeriods.map((period: string) =>
            validityPeriodStringToInt(period),
          ),
        }),
    },
  );

  const mappedData = mapSecurityClearances(data.records);

  return {
    securityClearances: mappedData,
    totalRecordCount: data.totalRecordCount,
  };
};

export const getSecurityClearanceById = async ({
  id,
  service,
}: GetSecurityClearanceByIdParams): Promise<SecurityClearanceDetails> => {
  return api.get(service, `security-clearance-requests/${id}`);
};

export const getMySecurityClearance = async ({
  service,
}: {
  service: string;
}): Promise<SecurityClearanceDetails> => {
  return api.get(service, "security-clearance/me");
};

export const submitSecurityClearanceRequest = async ({
  service,
  securityClearancePayload,
  securityClearanceRenewID,
  securityClearanceRequestStatus,
  transactionId,
}: {
  service: string;
  securityClearancePayload: SecurityClearanceRequestPayload;
  securityClearanceRenewID?: string;
  securityClearanceRequestStatus: string | null;
  transactionId?: string;
}) => {
  if (securityClearanceRenewID) {
    // Renew flow
    if (securityClearanceRequestStatus === "PENDING_RESUBMISSION") {
      return api.post(
        service,
        `security-clearance-requests:resubmit?existingSecurityClearanceId=${securityClearanceRenewID}`,
        securityClearancePayload,
        ...(transactionId ? [{ transactionId }] : []),
      );
    }

    return api.post(
      service,
      `security-clearance-requests:start?existingSecurityClearanceId=${securityClearanceRenewID}`,
      securityClearancePayload,
      ...(transactionId ? [{ transactionId }] : []),
    );
  } else {
    // Normal flow
    return api.post(
      service,
      "security-clearance-requests:start",
      securityClearancePayload,
      ...(transactionId ? [{ transactionId }] : []),
    );
  }
};

export const suspendSecurityClearanceById = async ({
  service,
  securityClearanceId,
  params,
  transactionId,
}: {
  service: string;
  securityClearanceId: string;
  params: SuspendSecurityClearanceByIdArgs;
  transactionId?: string;
}) => {
  return api.post(
    service,
    `security-clearance-requests/${securityClearanceId}:suspend`,
    { reason: params.suspensionReason },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const unsuspendSecurityClearanceById = async ({
  service,
  securityClearanceId,
  transactionId,
}: {
  service: string;
  securityClearanceId: string;
  transactionId?: string;
}) => {
  return api.post(
    service,
    `security-clearance-requests/${securityClearanceId}:unsuspend`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const completeSecurityClearanceById = async ({
  id,
  service,
  payload,
  transactionId,
}: {
  id: string;
  service: string;
  payload: CompleteSecurityClearancePayload;
  transactionId?: string;
}): Promise<SecurityClearanceDetails> => {
  return api.put(
    service,
    `security-clearance-requests/${id}:complete`,
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};
export const claimSecurityClearance = async ({
  service,
  securityClearanceId,
  transactionId,
}: ClaimSecurityClearanceParams & { transactionId?: string }) => {
  return api.put(
    service,
    `security-clearance-requests/${securityClearanceId}:claim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const permanentlyRejectSecurityClearanceById = async ({
  service,
  securityClearanceId,
  params,
  transactionId,
}: {
  service: string;
  securityClearanceId: string;
  params: { rejectionReason: string };
  transactionId?: string;
}) => {
  return api.post(
    service,
    `security-clearance-requests/${securityClearanceId}:permanently-reject`,
    { reason: params.rejectionReason },
    ...(transactionId ? [{ transactionId }] : []),
  );
};
export const unclaimSecurityClearance = async ({
  service,
  securityClearanceId,
  transactionId,
}: ClaimSecurityClearanceParams & { transactionId?: string }) => {
  return api.put(
    service,
    `security-clearance-requests/${securityClearanceId}:unclaim`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

/** Maximum number of items accepted by the bulk endpoints per request. */
export const SECURITY_CLEARANCE_BULK_MAX = 50;

export const bulkUpdateSecurityClearanceTasks = async ({
  service,
  taskIds,
  taskOperation,
  transactionId,
}: BulkUpdateSecurityClearanceTasksArgs & {
  transactionId?: string;
}): Promise<BulkSecurityClearanceResponse> => {
  return api.put(
    service,
    "security-clearance-requests:bulk",
    { taskIds, taskOperation },
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const bulkCompleteSecurityClearance = async (
  service: string,
  payload: BulkCompleteSecurityClearanceRequest,
  transactionId?: string,
): Promise<BulkSecurityClearanceResponse> => {
  return api.post(
    service,
    "security-clearance-requests/bulk:complete",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

/** Supervisor only. Permanently rejects the given clearances (reason required). */
export const bulkPermanentlyRejectSecurityClearance = async (
  service: string,
  payload: BulkSecurityClearanceIdsWithReasonRequest,
  transactionId?: string,
): Promise<BulkSecurityClearanceResponse> => {
  return api.post(
    service,
    "security-clearance-requests/bulk:permanently-reject",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

/** Operator + supervisor. Suspends the given approved clearances (reason required). */
export const bulkSuspendSecurityClearance = async (
  service: string,
  payload: BulkSecurityClearanceIdsWithReasonRequest,
  transactionId?: string,
): Promise<BulkSecurityClearanceResponse> => {
  return api.post(
    service,
    "security-clearance-requests/bulk:suspend",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

/** Operator + supervisor. Unsuspends the given suspended clearances. */
export const bulkUnsuspendSecurityClearance = async (
  service: string,
  payload: BulkSecurityClearanceIdsRequest,
  transactionId?: string,
): Promise<BulkSecurityClearanceResponse> => {
  return api.post(
    service,
    "security-clearance-requests/bulk:unsuspend",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

/** Supervisor only. Cancels the given security clearances. */
export const bulkCancelSecurityClearance = async (
  service: string,
  payload: BulkSecurityClearanceIdsRequest,
  transactionId?: string,
): Promise<BulkSecurityClearanceResponse> => {
  return api.post(
    service,
    "security-clearance-requests/bulk:cancel",
    payload,
    ...(transactionId ? [{ transactionId }] : []),
  );
};

export const cancelSecurityClearance = async ({
  service,
  securityClearanceId,
  transactionId,
}: CancelSecurityClearanceParams & { transactionId?: string }) => {
  return api.post(
    service,
    `security-clearance-requests/${securityClearanceId}:cancel`,
    ...(transactionId ? [undefined, { transactionId }] : []),
  );
};

export const checkPassportOrEmiratesId = async ({
  service,
  id,
  emiratesId,
  fullNameEnglish,
}: {
  service: string;
  id?: string;
  emiratesId?: string;
  /* Optional: applicant full name match validation (UAE PASS individual) */
  fullNameEnglish?: string;
}) => {
  return api.post(service, "security-clearance-requests/status", {
    passportNumber: id,
    emiratesIdNumber: emiratesId,
    // ignore if not needed (vendors/sponsor flows)
    fullNameEnglish,
  });
};

export const checkPublicPassportOrEmiratesId = async ({
  service,
  id,
  emiratesId,
}: {
  service: string;
  id?: string;
  emiratesId?: string;
}) => {
  return api.post(service, "security-clearance/public/me", {
    ...(id ? { passportNumber: id } : {}),
    ...(emiratesId ? { emiratesId } : {}),
  });
};

export const checkGatepassSecurityClearance = async ({
  service,
  passportNumber,
  emiratesId,
  expectedEntryDate,
  durationDays,
}: {
  service: string;
  passportNumber?: string;
  emiratesId?: string;
  expectedEntryDate?: string;
  durationDays?: number;
}): Promise<GatepassSecurityClearanceCheckResponse> => {
  const queryParams = new URLSearchParams();

  if (expectedEntryDate) {
    queryParams.set("expectedEntryDate", expectedEntryDate);
  }

  if (typeof durationDays === "number") {
    queryParams.set("durationDays", String(durationDays));
  }

  const query = queryParams.toString();
  const endpoint = query ? `security-clearance/check?${query}` : "security-clearance/check";

  return api.post(service, endpoint, {
    ...(passportNumber ? { passportNumber } : {}),
    ...(emiratesId ? { emiratesId } : {}),
  });
};

export const getSecurityClearancePrintDocument = async ({
  service,
  id,
}: {
  service: string;
  id: string;
}): Promise<Blob> => {
  return api.get(service, `download-self-introductory-document/${id}`, {
    responseType: "blob",
  });
};

export const getSecurityClearanceByUid = async ({
  service,
  uid,
}: {
  service: string;
  uid: string;
}): Promise<{ securityClearanceId: string }> => {
  return api.get(service, `security-clearance-requests/uid/${encodeURIComponent(uid)}`);
};
